/**
 * Delivery — report de bugs + pedidos de feature (kanban interno).
 */
import { api } from "@/lib/api";

interface Page<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

export type DeliveryKind = "bug" | "feature";
export type DeliveryStatus =
  | "backlog"
  | "analyzing"
  | "planned"
  | "in_progress"
  | "done"
  | "rejected";
export type DeliveryPriority = "low" | "medium" | "high" | "urgent";

export interface DeliveryItem {
  id: string;
  kind: DeliveryKind;
  status: DeliveryStatus;
  priority: DeliveryPriority;
  title: string;
  description: string | null;
  page_url: string | null;
  has_screenshot: boolean;
  reporter_name: string | null;
  reporter_email: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface DeliveryDetail extends DeliveryItem {
  screenshot_url: string | null;
}

export interface DeliveryCreate {
  kind: DeliveryKind;
  title: string;
  description?: string | null;
  priority?: DeliveryPriority;
  page_url?: string | null;
  screenshot_path?: string | null;
}

export interface DeliveryUpdate {
  kind?: DeliveryKind;
  status?: DeliveryStatus;
  priority?: DeliveryPriority;
  title?: string;
  description?: string | null;
  admin_notes?: string | null;
}

export const deliveryApi = {
  list: async (params: Record<string, unknown> = {}): Promise<Page<DeliveryItem>> => {
    const { data } = await api.get<Page<DeliveryItem>>("/v1/delivery", { params });
    return data;
  },
  get: async (id: string): Promise<DeliveryDetail> => {
    const { data } = await api.get<DeliveryDetail>(`/v1/delivery/${id}`);
    return data;
  },
  uploadUrl: async (
    filename: string,
    content_type: string,
  ): Promise<{ upload_url: string; storage_path: string; expires_at: string }> => {
    const { data } = await api.post("/v1/delivery/upload-url", { filename, content_type });
    return data;
  },
  create: async (body: DeliveryCreate): Promise<DeliveryDetail> => {
    const { data } = await api.post<DeliveryDetail>("/v1/delivery", body);
    return data;
  },
  update: async (id: string, body: DeliveryUpdate): Promise<DeliveryDetail> => {
    const { data } = await api.patch<DeliveryDetail>(`/v1/delivery/${id}`, body);
    return data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/v1/delivery/${id}`);
  },
};

export const DELIVERY_STATUS: { value: DeliveryStatus; label: string }[] = [
  { value: "backlog", label: "Recebido" },
  { value: "analyzing", label: "Em análise" },
  { value: "planned", label: "Planejado" },
  { value: "in_progress", label: "Em desenvolvimento" },
  { value: "done", label: "Entregue" },
  { value: "rejected", label: "Recusado" },
];

export const DELIVERY_STATUS_LABEL: Record<DeliveryStatus, string> = Object.fromEntries(
  DELIVERY_STATUS.map((s) => [s.value, s.label]),
) as Record<DeliveryStatus, string>;

export const DELIVERY_KIND_LABEL: Record<DeliveryKind, string> = {
  bug: "Bug",
  feature: "Feature",
};

export const DELIVERY_PRIORITY: { value: DeliveryPriority; label: string }[] = [
  { value: "low", label: "Baixa" },
  { value: "medium", label: "Média" },
  { value: "high", label: "Alta" },
  { value: "urgent", label: "Urgente" },
];

export const DELIVERY_PRIORITY_LABEL: Record<DeliveryPriority, string> = Object.fromEntries(
  DELIVERY_PRIORITY.map((p) => [p.value, p.label]),
) as Record<DeliveryPriority, string>;
